import type { ProductResponse } from '@malaberg/shared';
/**
 * ProductEditor — T27.
 *
 * Modal for creating or editing a product.
 * - Create mode: all fields editable, including ID (slug).
 * - Edit mode: ID is immutable.
 *
 * Fields:
 *   - Name (TextInput)
 *   - ID (slug, TextInput) — disabled in edit mode
 *   - Markets (TagsInput, ≥1 required)
 *   - SourcePackFilePicker (multi-select + reorder)
 */
import { Button, Group, Modal, Stack, TagsInput, Text, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { productsApi } from '../../api/endpoints/products';
import { type SelectedSourcePackFile, SourcePackFilePicker } from './SourcePackFilePicker';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Undefined when creating a new product. */
  product?: ProductResponse & { sourcePackFiles?: SelectedSourcePackFile[] };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ProductEditor({ opened, onClose, onSuccess, product }: Props) {
  const isEditMode = product !== undefined;

  const [name, setName] = useState(product?.name ?? '');
  const [id, setId] = useState(product?.id ?? '');
  const [markets, setMarkets] = useState<string[]>(product?.markets ?? []);
  const [sourcePackFiles, setSourcePackFiles] = useState<SelectedSourcePackFile[]>(
    product?.sourcePackFiles ?? []
  );
  const [marketsError, setMarketsError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [idError, setIdError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // The modal is a long-lived sibling that just toggles its `opened` prop,
  // so the useState initialisers above only fire on first mount — every
  // subsequent open with a different `product` keeps stale local state
  // (the symptom was the Edit modal opening with an empty Name field).
  // Resync from props whenever the modal transitions to open or the
  // product reference changes.
  useEffect(() => {
    if (!opened) return;
    setName(product?.name ?? '');
    setId(product?.id ?? '');
    setMarkets(product?.markets ?? []);
    setSourcePackFiles(product?.sourcePackFiles ?? []);
    setNameError(null);
    setIdError(null);
    setMarketsError(null);
    setSubmitError(null);
  }, [opened, product?.id]);

  function validate(): boolean {
    let valid = true;

    if (!name.trim()) {
      setNameError('Name is required');
      valid = false;
    } else {
      setNameError(null);
    }

    if (!isEditMode && !id.trim()) {
      setIdError('ID is required');
      valid = false;
    } else {
      setIdError(null);
    }

    if (markets.length === 0) {
      setMarketsError('At least one market is required');
      valid = false;
    } else {
      setMarketsError(null);
    }

    return valid;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setSubmitting(true);

    try {
      if (isEditMode) {
        await productsApi.update(product.id, {
          name: name.trim(),
          markets,
          sourcePackFiles: sourcePackFiles.map((f, index) => ({
            id: f.id,
            version: f.version,
            displayOrder: index,
          })),
        });
      } else {
        await productsApi.create({
          id: id.trim(),
          name: name.trim(),
          active: true,
          markets,
          sourcePackFiles: sourcePackFiles.map((f) => ({
            id: f.id,
            version: f.version,
          })),
        });
      }

      onSuccess();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    onClose();
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? `Edit product — ${product.name}` : 'New product'}
      centered
      size="lg"
    >
      <form onSubmit={handleSubmit} data-testid="product-editor">
        <Stack>
          <TextInput
            data-testid="product-name"
            label="Name"
            required
            value={name}
            onChange={(e) => {
              setName(e.currentTarget.value);
              setNameError(null);
            }}
            error={nameError}
          />

          <TextInput
            data-testid="product-id"
            label="ID (slug)"
            required={!isEditMode}
            value={isEditMode ? product.id : id}
            onChange={(e) => {
              if (!isEditMode) {
                setId(e.currentTarget.value);
                setIdError(null);
              }
            }}
            disabled={isEditMode}
            readOnly={isEditMode}
            description={
              isEditMode
                ? 'ID cannot be changed after creation'
                : 'Unique identifier (e.g. muscle-defense)'
            }
            error={idError}
          />

          <div>
            <TagsInput
              data-testid="product-markets"
              label="Markets"
              placeholder="Type a market and press Enter (e.g. US, UK)"
              value={markets}
              onChange={(vals) => {
                setMarkets(vals);
                if (vals.length > 0) setMarketsError(null);
              }}
              error={
                marketsError ? (
                  <span data-testid="product-markets-error">{marketsError}</span>
                ) : undefined
              }
            />
          </div>

          <SourcePackFilePicker
            value={sourcePackFiles}
            onChange={setSourcePackFiles}
            productId={id}
          />

          {submitError && (
            <Text c="red" size="sm" data-testid="product-editor-error">
              {submitError}
            </Text>
          )}

          <Group justify="flex-end">
            <Button
              data-testid="product-editor-cancel"
              variant="default"
              onClick={handleClose}
              type="button"
            >
              Cancel
            </Button>
            <Button data-testid="product-editor-save" type="submit" loading={submitting}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
